import { inject, Injectable } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { ComponentStore } from '@ngrx/component-store';
import { Store } from '@ngrx/store';
import { tuiIsPresent } from '@taiga-ui/cdk';
import { CartActions, CartItem, selectItemQuantityInCart } from '@us/web/cart/data-access';
import { CategoriesStore } from './categories.store';
import { selectItemQuantityInStock } from '@us/web/inventory/data-access';
import {
    ComboItem,
    DynamicState,
    Food,
    FoodOptionItem,
    FoodOptionSelectType,
    OrderItem,
    OrderItemOption,
    ProductType,
    Unit
} from '@us/web/shared/data-access';
import { MessageService } from '@us/web/shared/feature';
import { uniqueId } from '@us/web/shared/utils';
import { isNil } from 'lodash';
import {
    combineLatest,
    distinctUntilChanged,
    EMPTY,
    filter,
    Observable,
    switchMap,
    take,
    tap,
    withLatestFrom
} from 'rxjs';

export interface ProductState extends DynamicState<'product', Food | null> {
    productId: number | null;
    selectedValue: OrderItem | null;
    selectedUnitId: number | null;
    note: string;
    quantity: number;
    minimumQuantity: number;
    maximumQuantity: number;
    quantityInStock: number;
}

const initialState: ProductState = {
    productId: null,
    product: null,
    selectedValue: null,
    selectedUnitId: null,
    note: '',
    quantity: 1,
    minimumQuantity: 1,
    maximumQuantity: Number.MAX_SAFE_INTEGER,
    quantityInStock: 0,
    status: 'pending',
    error: null
};

@Injectable()
export class ProductStore extends ComponentStore<ProductState> {
    private readonly categoriesStore = inject(CategoriesStore);
    private readonly store = inject(Store);
    private readonly translocoService = inject(TranslocoService);
    private readonly messageService = inject(MessageService);

    readonly DEFAULT_IMAGE = '/assets/icons/picture.svg';

    constructor() {
        super({ ...initialState });
    }

    readonly productId$ = this.select(state => state.productId);
    readonly selectedValue$ = this.select(state => state.selectedValue);
    readonly product$ = this.productId$.pipe(
        filter(tuiIsPresent),
        distinctUntilChanged(),
        tap(() => {
            this.patchState({
                status: 'loading',
                error: null
            });
        }),
        // TODO: fetch product
        switchMap(productId => this.categoriesStore.item$(productId)),
        filter(tuiIsPresent),
        take(1),
        tap(product => this.setProduct(product)),
        tap(() => {
            this.patchState({
                status: 'success',
                error: null
            });
        }),
        switchMap(() => this.select(state => state.product))
    );

    readonly quantityInStock$ = this.productId$.pipe(
        filter(tuiIsPresent),
        switchMap(productId =>
            combineLatest([
                this.store.select(selectItemQuantityInStock(productId)),
                this.store.select(selectItemQuantityInCart(productId.toString()))
            ])
        ),
        tap(([quantityInStock, quantityInCart]) => {
            if (quantityInStock === -1) {
                this.patchState({
                    quantityInStock: -1
                });
            } else {
                const quantity = quantityInStock - quantityInCart;
                this.patchState({
                    quantityInStock: quantity
                });
            }
        }),
        switchMap(() => this.select(state => state.quantityInStock))
    );

    readonly minimumQuantity$ = this.select(state => state.minimumQuantity);
    readonly maximumQuantity$ = this.quantityInStock$.pipe(
        tap(quantityInStock => {
            this.patchState({
                maximumQuantity: quantityInStock === -1 ? Number.MAX_SAFE_INTEGER : quantityInStock
            });
        }),
        switchMap(() => this.select(state => state.maximumQuantity))
    );
    readonly note$ = this.select(state => state.note);
    readonly quantity$ = this.select(state => state.quantity);
    readonly productName$ = this.select(state => state.product?.name);
    readonly productDescription$ = this.select(state => state.product?.description);
    readonly productAvailable$ = this.select(state => state.product?.available);
    readonly units$ = this.select(state => state.product?.units || []);
    readonly options$ = this.select(state => state.product?.options || []);
    readonly comboList$ = this.select(state => state.product?.combo || []);

    readonly photos$ = this.select(state =>
        state.product?.photos?.length ? state.product?.photos : [this.DEFAULT_IMAGE]
    );

    readonly selectedUnitId$ = this.select(state => state.selectedUnitId);

    readonly selectedUnit$: Observable<Unit | undefined> = this.select(
        this.units$,
        this.selectedUnitId$,
        (units, selectedUnitId) => units.find(unit => unit.id === selectedUnitId)
    );

    readonly totalPrice$: Observable<number> = this.select(
        this.select(state => state.product),
        this.selectedUnit$,
        (product, selectedUnit) => {
            return (
                (selectedUnit?.price || 0) +
                (product?.options || []).reduce((acc, option) => {
                    if (option.select_type === FoodOptionSelectType.NotAllowSelected) {
                        return acc;
                    }

                    if (option.select_type === FoodOptionSelectType.AllowSelectedOne) {
                        return acc + (option.selected_item?.price || 0);
                    }

                    return acc + option.items.reduce((acc2, item) => acc2 + (item.selected ? item.price : 0), 0);
                }, 0)
            );
        }
    );

    readonly setProductId = this.updater(
        (state, productId: number): ProductState => ({
            ...state,
            productId
        })
    );

    readonly setProduct = this.updater((state, product: Food): ProductState => {
        const selectedUnitId = state.selectedValue?.unit.id;

        return {
            ...state,
            product: {
                ...product,
                options: (product.options || []).map(option => {
                    const selectedAmount =
                        state.selectedValue?.options?.filter(e => e.group_id === option.id).length || 0;

                    const selectedItem =
                        option.select_type === FoodOptionSelectType.AllowSelectedOne
                            ? state.selectedValue?.options?.find(e => e.group_id === option.id)
                            : null;

                    return {
                        ...option,
                        items: option.items.map(item => {
                            const found = state.selectedValue?.options?.find(
                                e => e.id === item.id && e.group_id === option.id
                            );

                            return {
                                ...item,
                                selected: !!found,
                                enabled: !!found || selectedAmount < option.max_choose
                            };
                        }),
                        selected_item: selectedItem
                            ? state.selectedValue?.options?.find(item => item.id === selectedItem.id)
                            : undefined
                    };
                }),
                combo: (product.combo || []).map(combo => {
                    const selectedAmount =
                        state.selectedValue?.combo?.filter(e => e.extra?.group_local_id === combo.id).length || 0;

                    const selectedItem =
                        combo.select_type === FoodOptionSelectType.AllowSelectedOne
                            ? state.selectedValue?.combo?.find(e => e.extra?.group_local_id === combo.id)
                            : null;

                    return {
                        ...combo,
                        items: combo.items.map(item => {
                            const found = state.selectedValue?.combo?.find(
                                e => e.product_id === item.id.toString() && e.extra?.group_local_id === combo.id
                            );

                            return {
                                ...item,
                                selected: !!found,
                                enabled: !!found || selectedAmount < combo.max_choose
                            };
                        }),
                        selected_item: selectedItem
                            ? combo.items.find(item => item.id.toString() === selectedItem.product_id)
                            : undefined
                    };
                })
            },
            selectedUnitId: selectedUnitId || (product.units.length > 0 ? product.units[0].id : null),
            quantity: state.selectedValue?.quantity || 1,
            note: state.selectedValue?.note || ''
        };
    });

    readonly setNote = this.updater(
        (state, note: string): ProductState => ({
            ...state,
            note
        })
    );

    readonly setQuantity = this.updater(
        (state, quantity: number): ProductState => ({
            ...state,
            quantity
        })
    );

    readonly setSelectedValue = this.updater(
        (state, selectedValue: OrderItem): ProductState => ({
            ...state,
            selectedValue
        })
    );

    readonly setUnitId = this.updater(
        (state, unitId: number): ProductState => ({
            ...state,
            selectedUnitId: unitId
        })
    );

    readonly setSelectedOptionItem = this.updater(
        (state, { optionIndex, value }: { optionIndex: number; value: FoodOptionItem }): ProductState => {
            const options = state.product?.options || [];
            if (
                isNil(options[optionIndex]) ||
                options[optionIndex].select_type !== FoodOptionSelectType.AllowSelectedOne
            ) {
                console.error('Not allow selected');
                return state;
            }

            options[optionIndex].selected_item = value;
            return {
                ...state,
                product: {
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    ...state.product!,
                    options
                }
            };
        }
    );

    readonly setSelectedComboItem = this.updater(
        (state, { comboIndex, value }: { comboIndex: number; value: ComboItem }): ProductState => {
            const combo = state.product?.combo || [];
            if (isNil(combo[comboIndex]) || combo[comboIndex].select_type !== FoodOptionSelectType.AllowSelectedOne) {
                console.error('Not allow selected');
                return state;
            }

            combo[comboIndex].selected_item = value;
            return {
                ...state,
                product: {
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    ...state.product!,
                    combo
                }
            };
        }
    );

    readonly toggleOptionItem = this.updater(
        (state, { optionIndex, optionItemIndex }: { optionIndex: number; optionItemIndex: number }): ProductState => {
            const options = state.product?.options || [];
            if (
                isNil(options[optionIndex]) ||
                isNil(options[optionIndex].items[optionItemIndex]) ||
                options[optionIndex].select_type !== FoodOptionSelectType.AllowSelectedMultiple
            ) {
                console.error('Not allow selected');
                return state;
            }

            options[optionIndex].items[optionItemIndex].selected =
                !options[optionIndex].items[optionItemIndex].selected;

            const selectedAmount = options[optionIndex].items.filter(e => e.selected).length;
            return {
                ...state,
                product: {
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    ...state.product!,
                    options: options.map((option, i) => ({
                        ...option,
                        items: option.items.map(item => ({
                            ...item,
                            enabled:
                                i === optionIndex ? item.selected || selectedAmount < option.max_choose : item.enabled
                        }))
                    }))
                }
            };
        }
    );

    readonly toggleComboItem = this.updater(
        (state, { comboIndex, comboItemIndex }: { comboIndex: number; comboItemIndex: number }): ProductState => {
            const combo = state.product?.combo || [];
            if (
                isNil(combo[comboIndex]) ||
                isNil(combo[comboIndex].items[comboItemIndex]) ||
                combo[comboIndex].select_type !== FoodOptionSelectType.AllowSelectedMultiple
            ) {
                console.error('Not allow selected');
                return state;
            }

            combo[comboIndex].items[comboItemIndex].selected = !combo[comboIndex].items[comboItemIndex].selected;

            const selectedAmount = combo[comboIndex].items.filter(e => e.selected).length;
            return {
                ...state,
                product: {
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    ...state.product!,
                    combo: combo.map((option, i) => ({
                        ...option,
                        items: option.items.map(item => ({
                            ...item,
                            enabled:
                                i === comboIndex ? item.selected || selectedAmount < option.max_choose : item.enabled
                        }))
                    }))
                }
            };
        }
    );

    readonly addToCart = this.effect<{ checkout?: boolean; redirectUrl?: string }>(params$ =>
        params$.pipe(
            withLatestFrom(this.product$, this.selectedUnitId$, this.quantity$, this.note$, this.selectedValue$),
            switchMap(([{ redirectUrl }, product, selectedUnitId, quantity, note, selectedValue]) => {
                if (!product) {
                    return EMPTY;
                }

                const selectedUnit = product?.units.find(unit => unit.id === selectedUnitId);
                if (!selectedUnit) {
                    this.messageService.error(this.translocoService.translate('please_select_a_unit'));
                    return EMPTY;
                }

                const newCartItem: CartItem = {
                    uuid: selectedValue?.uuid,
                    product_id: product?.id.toString() || '',
                    product_name: product?.name || '',
                    product_type: product?.type || ProductType.Goods,
                    unit: selectedUnit,
                    price: selectedUnit.price,
                    quantity: quantity - (selectedValue?.quantity || 0),
                    note,
                    specifications: note,
                    options: [],
                    version_code: 0,
                    combo: []
                };

                for (let i = 0; i < (product ? product.options?.length : 0); i++) {
                    const option = product?.options[i];
                    if (!option) {
                        continue;
                    }
                    switch (option.select_type) {
                        case FoodOptionSelectType.AllowSelectedOne:
                            if (!option.selected_item) {
                                this.messageService.error(
                                    this.translocoService.translate('please_select_a_option', { name: option.title })
                                );
                                return EMPTY;
                            }

                            // eslint-disable-next-line no-case-declarations
                            const selectedOption: OrderItemOption = {
                                group_id: option.id || '',
                                id: option.selected_item.id,
                                // name: `${option.title}: ${option.selected_item.name}`,
                                name: option.selected_item.name,
                                quantity: 1,
                                price: option.selected_item.price
                            };

                            newCartItem.options?.push(selectedOption);

                            newCartItem.price += selectedOption.price * selectedOption.quantity;
                            break;
                        case FoodOptionSelectType.AllowSelectedMultiple:
                        case FoodOptionSelectType.NotAllowSelected:
                        default:
                            for (let j = 0; j < option.items.length; j++) {
                                if (
                                    option.items[j].selected ||
                                    option.select_type === FoodOptionSelectType.NotAllowSelected
                                ) {
                                    const selectedOption: OrderItemOption = {
                                        group_id: option.id || '',
                                        id: option.items[j].id,
                                        // name: `${option.title}: ${option.items[j].name}`,
                                        name: option.items[j].name,
                                        quantity: 1,
                                        price: option.items[j].price
                                    };

                                    newCartItem.options?.push(selectedOption);
                                    newCartItem.price += selectedOption.price;
                                }
                            }
                            break;
                    }
                }

                if (product?.combo !== null && product.combo.length > 0) {
                    // TODO: handle selected value
                    for (let i = 0; i < (product && product.combo ? product.combo?.length : 0); i++) {
                        const comboElement = product?.combo[i];
                        if (!comboElement) {
                            continue;
                        }
                        switch (comboElement.select_type) {
                            case FoodOptionSelectType.AllowSelectedOne:
                                if (!comboElement.selected_item) {
                                    this.messageService.error(
                                        this.translocoService.translate('please_select_a_option', {
                                            name: comboElement.title
                                        })
                                    );
                                    return EMPTY;
                                }

                                newCartItem.combo?.push({
                                    uuid: uniqueId(),
                                    product_id: comboElement.selected_item.id.toString() || '',
                                    product_name: comboElement.selected_item.name || '',
                                    product_type: ProductType.Goods, // TODO
                                    unit: comboElement.selected_item.units.find(e => e.is_primary) as Unit, // TODO
                                    price: 0,
                                    quantity: 1,
                                    note,
                                    specifications: note,
                                    options: [],
                                    version_code: 0,
                                    extra: {
                                        group_local_id: comboElement.id,
                                        unit_exchange: 1,
                                        quantitative: 1,
                                        combo_price: comboElement.selected_item.price,
                                        vat_percent: 0,
                                        vat_price: 0
                                    }
                                });
                                break;
                            case FoodOptionSelectType.AllowSelectedMultiple:
                            case FoodOptionSelectType.NotAllowSelected:
                            default:
                                for (let j = 0; j < comboElement.items.length; j++) {
                                    if (
                                        comboElement.items[j].selected ||
                                        comboElement.select_type === FoodOptionSelectType.NotAllowSelected
                                    ) {
                                        newCartItem.combo?.push({
                                            uuid: uniqueId(),
                                            product_id: comboElement.items[j].id.toString() || '',
                                            product_name: comboElement.items[j].name || '',
                                            product_type: 1, // TODO
                                            unit: comboElement.items[j].units.find(e => e.is_primary) as Unit, // TODO
                                            price: 0, // comboElement.items[j].price,
                                            quantity: 1,
                                            note,
                                            specifications: note,
                                            options: [],
                                            version_code: 0,
                                            extra: {
                                                group_local_id: comboElement.id,
                                                unit_exchange: 1,
                                                quantitative: 1,
                                                combo_price: comboElement.items[j].price,
                                                vat_percent: 0,
                                                vat_price: 0
                                            }
                                        });
                                    }
                                }
                                break;
                        }
                    }
                }
                this.store.dispatch(CartActions.addToCart({ item: newCartItem, redirectUrl }));
                this.setProduct(product);
                this.patchState({
                    quantity: 1,
                    note: ''
                });
                return EMPTY;
            })
        )
    );

    readonly decreaseQuantity = this.updater(
        (state): ProductState => ({
            ...state,
            quantity: Math.max(state.minimumQuantity, state.quantity - 1)
        })
    );

    readonly increaseQuantity = this.updater((state): ProductState => {
        return {
            ...state,
            quantity: Math.min(state.maximumQuantity, state.quantity + 1)
        };
    });
}
